import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import AnonymousOnly from '@/components/AnonymousOnly';
import RequireAuth from '@/components/RequireAuth';
import RequireAgent from '@/components/common/RequireAgent';
import RequireCarrier from '@/components/common/RequireCarrier';
import RequireNonAgent from '@/components/common/RequireNonAgent';
import RequireNonCarrier from '@/components/common/RequireNonCarrier';
import AppLayout from '@/layouts/AppLayout';
import AuthLayout from '@/layouts/AuthLayout';
import PublicLayout from '@/layouts/PublicLayout';
import About from '@/pages/About';
import AppHome from '@/pages/AppHome';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import NotFound from '@/pages/NotFound';
import StaticDocPage from '@/pages/StaticDocPage';
import DealViewPage from '@/pages/app/deals/DealViewPage';

// Чат — отдельный lazy-чанк: тянет matrix-js-sdk, не должен попадать в initial-бандл.
const ChatPage = lazy(() => import('@/pages/app/chat/ChatPage'));
// Дашборд агента — отдельный lazy-чанк: тянет @mantine/charts (recharts), не в initial-бандл.
const AgentDashboardPage = lazy(() => import('@/pages/app/agent/AgentDashboardPage'));
// Кабинет перевозчика — отдельный lazy-чанк как у сиблингов-разделов по роли; не в initial-бандл
// для не-перевозчиков и не растит его по мере наполнения кабинета (§5.2+).
const CarrierHomePage = lazy(() => import('@/pages/app/carrier/CarrierHomePage'));
// Списки заявок перевозчика — отдельные lazy-чанки (тела наполняет §5.7).
const CarrierNewRequestsPage = lazy(() => import('@/pages/app/carrier/CarrierNewRequestsPage'));
const CarrierInWorkPage = lazy(() => import('@/pages/app/carrier/CarrierInWorkPage'));
const CarrierDonePage = lazy(() => import('@/pages/app/carrier/CarrierDonePage'));
const CarrierAdminGuidePage = lazy(() => import('@/pages/app/carrier/CarrierAdminGuidePage'));
const CarrierDevGuidePage = lazy(() => import('@/pages/app/carrier/CarrierDevGuidePage'));
const CarrierRequestDetailPage = lazy(() => import('@/pages/app/carrier/CarrierRequestDetailPage'));
const CarrierTakePage = lazy(() => import('@/pages/app/carrier/CarrierTakePage'));
const AuditLogPage = lazy(() => import('@/pages/app/audit/AuditLogPage'));
import DealsListPage from '@/pages/app/deals/DealsListPage';
import BuyGroupPage from '@/pages/app/buy/BuyGroupPage';
import BestPriceSearchPage from '@/pages/app/buy/BestPriceSearchPage';
import BuyListPage from '@/pages/app/buy/BuyListPage';
import OfferPreviewPage from '@/pages/app/buy/OfferPreviewPage';
import PriceListFormPage from '@/pages/app/buy/PriceListFormPage';
import SellBestPriceSearchPage from '@/pages/app/sell/SellBestPriceSearchPage';
import SellListPage from '@/pages/app/sell/SellListPage';
import SellOfferFormPage from '@/pages/app/sell/SellOfferFormPage';
import SellOfferView from '@/pages/app/sell/SellOfferView';
import PurchaseOfferPreviewPage from '@/pages/app/sell/PurchaseOfferPreviewPage';
import AccreditationTab from '@/pages/app/company/AccreditationTab';
import AgencyTab from '@/pages/app/company/AgencyTab';
import EmployeesTab from '@/pages/app/company/EmployeesTab';
import BankingTab from '@/pages/app/company/BankingTab';
import CompanyCard from '@/pages/app/company/CompanyCard';
import CompanyLayout from '@/pages/app/company/CompanyLayout';
import ApiAccessRoute from '@/pages/app/profile/ApiAccessRoute';
import ProfileLayout from '@/pages/app/profile/ProfileLayout';
import ProfileTab from '@/pages/app/profile/ProfileTab';
import SecurityTab from '@/pages/app/profile/SecurityTab';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import Register from '@/pages/auth/Register';
import ResetPassword from '@/pages/auth/ResetPassword';
import EmployeeInvitation from '@/pages/auth/EmployeeInvitation';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import ImpersonationCallback from '@/pages/auth/ImpersonationCallback';
import ErrorScreen from '@/components/ErrorScreen';
import RouteShell from './RouteShell';
import { ROUTES } from './paths';

export const router = createBrowserRouter(
  [
    {
      element: <RouteShell />,
      // Без этого ошибка отрисовки страницы показала бы служебный экран маршрутизатора.
      errorElement: <ErrorScreen />,
      children: [
    {
      element: <PublicLayout />,
      children: [
        { path: ROUTES.HOME, element: <Landing /> },
        { path: ROUTES.ABOUT, element: <About /> },
        { path: ROUTES.NOT_FOUND, element: <NotFound /> },
        { path: ROUTES.POLICY, element: <StaticDocPage docKey="policy" /> },
        { path: ROUTES.TERMS, element: <StaticDocPage docKey="terms" /> },
        { path: ROUTES.SUPPLY, element: <StaticDocPage docKey="supply" /> },
        { path: ROUTES.TEO, element: <StaticDocPage docKey="teo" /> },
      ],
    },
    {
      element: <AuthLayout />,
      children: [
        // Экраны входа/регистрации/восстановления недоступны залогиненному пользователю.
        {
          element: <AnonymousOnly />,
          children: [
            { path: ROUTES.LOGIN, element: <Login /> },
            { path: ROUTES.REGISTER, element: <Register /> },
            { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPassword /> },
            { path: '/auth/reset-password/:id', element: <ResetPassword /> },
          ],
        },
        // Подтверждение email обрабатывает токен из письма даже при наличии старой сессии
        // (само её очищает), поэтому НЕ под AnonymousOnly.
        { path: '/auth/verify-email/:id', element: <VerifyEmail /> },
        { path: '/auth/invitation/:id', element: <VerifyEmail /> },
        // Приём приглашения сотрудника: учётной записи ещё нет, но у человека может быть открыта
        // чужая сессия на общем компьютере — поэтому НЕ под AnonymousOnly, страница сама войдёт
        // под новой учётной записью.
        { path: '/auth/employee-invitation/:id', element: <EmployeeInvitation /> },
        // Приём имперсонации: меняет ?code на сессию, сам чистит старую сессию → НЕ под AnonymousOnly.
        { path: ROUTES.IMPERSONATION_CALLBACK, element: <ImpersonationCallback /> },
      ],
    },
    {
      element: <RequireAuth />,
      children: [
        {
          element: <AppLayout />,
          children: [
            // Неизвестный адрес кабинета: остаёмся в кабинете, с меню, и адрес не теряется.
            { path: `${ROUTES.APP}/*`, element: <NotFound /> },
            {
              // Перевозчик не видит общую «Главную» покупателя/продавца — уводим в его кабинет.
              element: <RequireNonCarrier />,
              children: [{ path: ROUTES.APP, element: <AppHome /> }],
            },
            { path: ROUTES.APP_AUDIT, element: <AuditLogPage /> },
            {
              path: ROUTES.APP_PROFILE,
              element: <ProfileLayout />,
              children: [
                { index: true, element: <ProfileTab /> },
                { path: 'security', element: <SecurityTab /> },
                // API-доступ не нужен членам агентской организации — агента уводим на профиль.
                {
                  element: <RequireNonAgent redirectTo={ROUTES.APP_PROFILE} />,
                  children: [{ path: 'api-access', element: <ApiAccessRoute /> }],
                },
              ],
            },
            {
              path: ROUTES.APP_COMPANY,
              element: <CompanyLayout />,
              children: [
                { index: true, element: <CompanyCard /> },
                { path: 'banking', element: <BankingTab /> },
                // Аттестация и агентский договор агенту не нужны — уводим на карточку компании.
                {
                  element: <RequireNonAgent redirectTo={ROUTES.APP_COMPANY} />,
                  children: [
                    { path: 'accreditation', element: <AccreditationTab /> },
                    // Агентский договор не нужен ещё и перевозчику — закрываем и от него.
                    {
                      element: <RequireNonCarrier redirectTo={ROUTES.APP_COMPANY} />,
                      children: [{ path: 'agency', element: <AgencyTab /> }],
                    },
                  ],
                },
                { path: 'employees', element: <EmployeesTab /> },
              ],
            },
            { path: ROUTES.APP_BUY, element: <BuyListPage /> },
            { path: ROUTES.APP_BUY_GROUP, element: <BuyGroupPage /> },
            { path: ROUTES.APP_BUY_OFFER, element: <OfferPreviewPage /> },
            { path: ROUTES.APP_BUY_NEW, element: <PriceListFormPage /> },
            { path: ROUTES.APP_BUY_EDIT, element: <PriceListFormPage /> },
            { path: ROUTES.APP_SELL, element: <SellListPage /> },
            { path: ROUTES.APP_SELL_OFFER, element: <SellOfferView /> },
            { path: ROUTES.APP_SELL_COUNTER, element: <PurchaseOfferPreviewPage /> },
            { path: ROUTES.APP_SELL_BEST_PRICE, element: <SellBestPriceSearchPage /> },
            { path: ROUTES.APP_SELL_NEW, element: <SellOfferFormPage /> },
            { path: ROUTES.APP_SELL_EDIT, element: <SellOfferFormPage /> },
            { path: ROUTES.APP_DEALS, element: <DealsListPage /> },
            { path: `${ROUTES.APP_DEALS}/:dealId`, element: <DealViewPage /> },
            {
              path: ROUTES.APP_CHAT,
              element: (
                <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                  <ChatPage />
                </Suspense>
              ),
            },
            { path: ROUTES.APP_BEST_PRICE, element: <BestPriceSearchPage /> },
            {
              element: <RequireAgent />,
              children: [
                {
                  path: ROUTES.APP_AGENT_DASHBOARD,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <AgentDashboardPage />
                    </Suspense>
                  ),
                },
              ],
            },
            {
              element: <RequireCarrier />,
              children: [
                {
                  path: ROUTES.APP_CARRIER,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierHomePage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.APP_CARRIER_NEW,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierNewRequestsPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.APP_CARRIER_IN_WORK,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierInWorkPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.APP_CARRIER_DONE,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierDonePage />
                    </Suspense>
                  ),
                },
                {
                  path: `${ROUTES.APP_CARRIER_REQUESTS}/:id`,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierRequestDetailPage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.APP_CARRIER_REQUEST_TAKE,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierTakePage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.APP_CARRIER_INTEGRATION_ADMIN,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierAdminGuidePage />
                    </Suspense>
                  ),
                },
                {
                  path: ROUTES.APP_CARRIER_INTEGRATION_DEV,
                  element: (
                    <Suspense fallback={<Center h="60dvh"><Loader /></Center>}>
                      <CarrierDevGuidePage />
                    </Suspense>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
    { path: '*', element: <Navigate to={ROUTES.NOT_FOUND} replace /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);
